import {
  detectStoryblokEditorClient,
  resolveStoryblokFrameState,
} from '../../utils/storyblok'

/**
 * The one signal for "this page is being rendered inside the Storyblok Visual
 * Editor" (ECOM-2791). Consumers use it to decide whether links should be
 * navigable: inert inside the editor so click-to-edit selects the blok, live
 * everywhere else — including a preview URL opened in a standalone tab, which
 * is the bug this replaces three divergent local checks to fix.
 *
 * Strictly about link interactivity. Draft-vs-published content resolution
 * keeps keying off `_storyblok` on its own, so an out-of-frame preview still
 * renders unpublished content.
 *
 * The value settles in two steps:
 *  - On the server, from the `Sec-Fetch-Dest` request header, so the first
 *    paint already carries the right markup.
 *  - On the client in `onMounted` — not at setup — so hydration renders the
 *    payload value unchanged and the correction lands as an ordinary reactive
 *    update instead of a hydration mismatch. This also overrides any value
 *    baked in by a `_payload.json` prefetch, whose `Sec-Fetch-Dest` is `empty`.
 */
export const useStoryblokEditor = () => {
  const route = useRoute()
  // Truthiness rather than `'_storyblok' in query`, so this agrees with the
  // draft-vs-published rule in `../../utils/cms.ts`. A genuine preview URL
  // always carries `_storyblok=<story id>`; a bare valueless `?_storyblok`
  // resolves as *published* content there, so treating it as an editor preview
  // here would cripple the links on a page serving published content — which is
  // what `apps/ags/storyblok/GoldnerClubEntryPageActions.vue` pushes customers
  // onto.
  const isPreviewRoute = computed(() => !!route.query._storyblok)

  // Holds one question only: is this *document* framed by the editor? That is a
  // property of the document, not of the route, which is why the route check
  // lives in the returned computed instead of in here. Sharing it means one
  // detection serves every CMSLink and ProductCard on the page.
  //
  // The default is keyed to `isPreviewRoute` rather than a blind `true` so that
  // a first touch on an ordinary page starts live instead of rendering a tick of
  // inert links.
  const frameState = useState('storyblok-editor', () => isPreviewRoute.value)

  if (import.meta.server) {
    const headers = useRequestHeaders(['sec-fetch-dest'])
    frameState.value = resolveStoryblokFrameState({
      isPreviewRoute: isPreviewRoute.value,
      secFetchDest: headers['sec-fetch-dest'],
    })
  }

  /**
   * Only a preview route may write the shared answer. `detectStoryblokEditorClient`
   * reports `false` for a non-preview route by contract, so letting an ordinary
   * page write would erase a `true` resolved inside the editor: an editor user
   * clicking a live category link mounts a PLP full of product cards, each
   * overwriting the state, and a layout-level consumer that never remounts would
   * then render navigable links the next time a preview route came back.
   */
  const resolveOnClient = () => {
    if (!isPreviewRoute.value) {
      return
    }
    frameState.value = detectStoryblokEditorClient({
      isPreviewRoute: true,
      win: window,
    })
  }

  if (import.meta.client) {
    // `onMounted` rather than setup keeps hydration rendering the server value,
    // so the correction lands as an ordinary reactive update instead of a
    // mismatch. It covers a component's own first paint.
    onMounted(resolveOnClient)
    // The watcher covers a client-side navigation *onto* a preview route. A
    // `useState` initialiser only runs on first touch, so on that navigation the
    // retained value from the previous page is what renders — already-mounted
    // layout consumers re-resolve here before the incoming page paints.
    watch(isPreviewRoute, resolveOnClient)
  }

  return {
    isInStoryblokEditor: computed(
      () => isPreviewRoute.value && frameState.value,
    ),
  }
}
